import { Fragment, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Modal, ModalBody, ModalFooter } from "reactstrap";
import Loading from "components/Loading";
import { ReactTable } from "components/Table";
import { saveEditResponsiblePerson } from "actions/shopActions";

function ModalChangeResponsiblePerson(props) {
    const { t } = useTranslation();
    const { changeResponsiblePerson, isOpenModalChangeResponsiblePerson, openModal, saveEditResponsiblePerson } = props;
    const { isLoading } = props.shop;

    const data = useMemo(() => {
        const list = changeResponsiblePerson?.responsible_persons || [];
        return list.map((rp) => {
            const profiles = Array.isArray(rp?.regional_profiles) ? rp.regional_profiles : [];
            const primaryProfile =
                profiles.find((p) => p?.locale === "de-DE") ||
                profiles.find((p) => p?.locale === "en-IE") ||
                profiles[0] ||
                {};
            const phoneCode = primaryProfile?.phone_number?.country_code || "";
            const phoneLocal = primaryProfile?.phone_number?.local_number || "";
            const phone = [phoneCode, phoneLocal].filter(Boolean).join(" ");
            const addr = primaryProfile?.address || {};
            const addressText = [
                addr?.street_address_line1,
                addr?.city,
                addr?.province,
                addr?.postal_code,
                addr?.country,
            ]
                .filter(Boolean)
                .join(", ");

            return {
                ...rp,
                name: primaryProfile?.name || "",
                email: primaryProfile?.email || "",
                phone,
                addressText,
                locale: primaryProfile?.locale || "",
                locales: profiles.map((p) => p?.locale).filter(Boolean).join(", "),
                profile_count: profiles.length,
            };
        });
    }, [changeResponsiblePerson]);
    const _id = changeResponsiblePerson?._id;
    const [selected, setSelected] = useState(data.filter((v) => v?.is_default).map((v) => v.id) || []);

    const columns = [
        { dataField: "id", text: "#", editable: false, formatter: (_c, _r, i) => i + 1 },
        { dataField: "name", text: t("Name"), editable: false },
        { dataField: "phone", text: t("Phone"), editable: false },
        { dataField: "email", text: t("Email"), editable: false },
        { dataField: "locale", text: t("Primary Locale"), editable: false },
        { dataField: "profile_count", text: t("Profiles"), editable: false },
        {
            dataField: "locales",
            text: t("Locales"),
            editable: false,
            formatter: (cell) => (
                <span
                    title={cell}
                    style={{
                        maxWidth: "260px",
                        display: "inline-block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {cell}
                </span>
            ),
        },
        {
            dataField: "addressText",
            text: t("Address"),
            editable: false,
            formatter: (cell) => (
                <span
                    title={cell}
                    style={{
                        maxWidth: "320px",
                        display: "inline-block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {cell}
                </span>
            ),
        },
        {
            dataField: "is_default",
            text: t("Is Default"),
            editable: false,
            formatter: (cell) => (cell ? "Yes" : "No"),
        },
    ];

    const onClickSave = () => {
        saveEditResponsiblePerson({ _id, responsiblePersonSelected: selected }, openModal);
    };

    const selectRow = {
        mode: "checkbox",
        clickToSelect: true,
        selected: selected || [],
        onSelect: (row, isSelect) => {
            if (isSelect) {
                setSelected((prev) => [...new Set([...(prev || []), row?.id])]);
            } else {
                setSelected((prev) => (prev || []).filter((id) => id !== row?.id));
            }
        },
        onSelectAll: (isSelect, rows) => {
            if (isSelect) {
                setSelected((rows || []).map((r) => r?.id).filter(Boolean));
            } else {
                setSelected([]);
            }
        },
    };

    return (
        <Fragment>
            <Modal centered size="xl" toggle={() => openModal()} isOpen={isOpenModalChangeResponsiblePerson}>
                <Loading isLoading={isLoading} />
                <div className="modal-header">
                    <h1 className="mb-0 text-dark">{t("Change Responsible Person")}</h1>
                    <div className="btn btn-sm btn-icon btn-active-color-primary" data-bs-dismiss="modal">
                        <i className="ki-outline ki-cross fs-1" onClick={() => openModal()}></i>
                    </div>
                </div>
                <ModalBody>
                    <div className="al-variants-table">
                        <ReactTable
                            keyField="id"
                            isSearchBar={false}
                            columns={columns}
                            data={data}
                            alClassName="table-layout-inherit"
                            isSelectRow={true}
                            selectRow={selectRow}
                        />
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button type="submit" className="btn btn-primary" onClick={onClickSave} disabled={!selected?.length}>
                        {t("Change")}
                    </button>
                    <button type="reset" className="btn btn-active-light me-3" onClick={() => openModal()}>
                        {t("Cancel")}
                    </button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
}

ModalChangeResponsiblePerson.propTypes = {
    saveEditResponsiblePerson: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
    shop: state.shop,
});

const mapDispatchToProps = {
    saveEditResponsiblePerson,
};

export default connect(mapStateToProps, mapDispatchToProps)(ModalChangeResponsiblePerson);
